import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { AdminAuditEntry } from '../../core/models/admin-audit.model';
import { JsonPipe } from '@angular/common';

/** Représente un rôle extrait du champ details.representation */
interface ExtractedRole {
  name: string;
  clientRole: boolean;
  composite: boolean;
}

@Component({
  selector: 'app-admin-audit-detail-dialog',
  standalone: true,
  imports: [MatDialogModule, JsonPipe],
  template: `
    <!-- ── Header ── -->
     @if (loading) {
      <!-- ── Skeleton de chargement ── -->
      <div class="dialog-skeleton">
        <div class="sk-header">
          <div class="sk-avatar"></div>
          <div class="sk-header-text">
            <div class="sk-line sk-line--title"></div>
            <div class="sk-line sk-line--subtitle"></div>
          </div>
        </div>
        <div class="sk-banner"></div>
        <div class="sk-body">
          <div class="sk-section-title"></div>
          <div class="sk-cards">
            <div class="sk-card"></div>
          </div>
          <div class="sk-section-title"></div>
          <div class="sk-row"></div>
        </div>
      </div>
    }
    
    @if (!loading) {
      <div class="dialog-header">
        <div class="header-icon-wrap">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 11V16M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z"
              stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
              <path d="M12 8H12.01" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="header-text">
            <span class="header-title">Détails de l'événement</span>
            <span class="header-subtitle">{{ formatDate(data.timestamp) }}</span>
          </div>
          <button class="close-btn" mat-dialog-close aria-label="Fermer">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
      }
    
      <!-- ── Bannière opération ── -->
      <div class="operation-banner" [attr.data-type]="data.operationType">
        <span class="op-label">{{ getOperationLabel(data.operationType) }}</span>
        <span class="op-type">{{ data.operationType }}</span>
      </div>
    
      <mat-dialog-content class="dialog-body">
    
        <!-- ── Acteurs ── -->
        <div class="section-title">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M17 20H22V18C22 16.3431 20.6569 15 19 15C18.0444 15 17.1931 15.4468 16.6438 16.1429M17 20H7M17 20V18C17 17.3438 16.8736 16.717 16.6438 16.1429M7 20H2V18C2 16.3431 3.34315 15 5 15C5.95561 15 6.80686 15.4468 7.35625 16.1429M7 20V18C7 17.3438 7.12642 16.717 7.35625 16.1429M7.35625 16.1429C7.99353 14.301 9.84315 13 12 13C14.1569 13 16.0065 14.301 16.6438 16.1429M15 7C15 8.65685 13.6569 10 12 10C10.3431 10 9 8.65685 9 7C9 5.34315 10.3431 4 12 4C13.6569 4 15 5.34315 15 7ZM21 10C21 11.1046 20.1046 12 19 12C17.8954 12 17 11.1046 17 10C17 8.89543 17.8954 8 19 8C20.1046 8 21 8.89543 21 10ZM7 10C7 11.1046 6.10457 12 5 12C3.89543 12 3 11.1046 3 10C3 8.89543 3.89543 8 5 8C6.10457 8 7 8.89543 7 10Z"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Acteurs
          </div>
          <div class="cards-row">
            <div class="info-card actor-card">
              <div class="card-icon author-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" stroke-width="1.8"/>
                  <path d="M20 21C20 18.2386 16.4183 16 12 16C7.58172 16 4 18.2386 4 21" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                </svg>
              </div>
              <div class="card-content">
                <span class="card-label">Auteur</span>
                <span class="card-value">{{ data.auteur || '—' }}</span>
              </div>
            </div>
            <div class="info-card target-card">
              <div class="card-icon target-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/>
                  <circle cx="12" cy="12" r="4" stroke="currentColor" stroke-width="1.8"/>
                  <circle cx="12" cy="12" r="1" fill="currentColor"/>
                </svg>
              </div>
              <div class="card-content">
                <span class="card-label">Cible</span>
                <span class="card-value">{{ data.cible || '—' }}</span>
              </div>
            </div>
          </div>

          <!-- ══════════════════════════════════════════════════════════════
              SECTION CHANGEMENTS — affichée uniquement pour UPDATE
              ══════════════════════════════════════════════════════════════ -->
          @if (data.operationType === 'UPDATE' && data.changes?.length) {
            <div class="section-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"
                      stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              Changements détectés
              <span class="roles-count-badge">{{ data.changes.length }}</span>
            </div>

            <div class="changes-list">
              @for (change of data.changes; track change.field) {
                <div class="change-row" [attr.data-category]="change.category">
                  <span class="change-category-dot"></span>

                  <div class="change-main">
                    <span class="change-label">{{ change.fieldLabel }}</span>

                    <div class="change-values">
                      @if (change.oldValue !== null) {
                        <span class="change-old">{{ change.oldValue }}</span>
                        <span class="change-arrow">→</span>
                      }
                      @if (change.newValue !== null) {
                        <span class="change-new">{{ change.newValue }}</span>
                      } @else {
                        <span class="change-new change-new--empty">(vide)</span>
                      }
                    </div>
                  </div>

                  <span class="change-type-badge" [attr.data-type]="change.changeType">
                    {{ getChangeTypeLabel(change.changeType) }}
                  </span>
                </div>
              }
            </div>
          }

          @if (data.operationType === 'UPDATE' && !data.changes?.length) {
            <div class="no-roles-msg">
              @if (hasBothRepresentations) {
                Aucun changement détecté : les deux états (avant/après) sont identiques.
              } @else if (hasAnyRepresentation) {
                Une seule représentation est disponible dans l'événement.
                Le détail précis de la modification ne peut pas être calculé.
              } @else {
                L'API Keycloak n'a retourné ni <code>representation</code>
                ni <code>oldRepresentation</code>. Activez
                <strong>« Include Representation »</strong> dans
                <strong>Realm Settings → Events → Admin Events</strong>.
              }
            </div>
          }

          @if (data.operationType === 'ACTION') {
            <div class="section-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"
                      stroke="currentColor" stroke-width="1.5"
                      stroke-linejoin="round"/>
              </svg>
              Nature de l'action
            </div>
            <div class="action-detail">
              <div class="action-detail-title">
                @if (data.actionSubType) {
                  {{ data.summary || data.actionSubType }}
                } @else {
                  Action administrative
                  <span class="action-detail-subtitle">(sous-type non identifié)</span>
                }
              </div>
              @if (data.actionSubType) {
                <div class="action-detail-code">
                  Code : <code>{{ data.actionSubType }}</code>
                </div>
              } @else {
                <div class="action-detail-code">
                  L'API Keycloak n'a pas fourni <code>details.action</code> et aucune
                  inférence n'a été possible.
                </div>
              }
            </div>
          }
    
          <!-- ══════════════════════════════════════════════════════
          SECTION RÔLES — affichée uniquement pour AFFECTATION et RETRAIT
          Les rôles sont extraits depuis details.representation (JSON array)
          ══════════════════════════════════════════════════════ -->
          @if (isRoleOperation && extractedRoles.length > 0) {
            <div class="section-title" [class.section-title--add]="data.operationType === 'AFFECTATION'"
              [class.section-title--remove]="data.operationType === 'RETRAIT'">
              <!-- Icône clé pour AFFECTATION, croix pour RETRAIT -->
              @if (data.operationType === 'AFFECTATION') {
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M15.5 5C17.9853 5 20 7.01472 20 9.5C20 11.9853 17.9853 14 15.5 14C13.8265 14 12.3643 13.0777 11.5714 11.7143L5 11.7143V14H3V11.7143H2V9.5H11.5714C12.3643 8.13567 13.8265 7.21429 15.5 7.21429"
                    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="15.5" cy="9.5" r="1.5" fill="currentColor"/>
                  </svg>
                }
                @if (data.operationType === 'RETRAIT') {
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M15 9L9 15M9 9L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                      stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    </svg>
                  }
                  {{ data.operationType === 'AFFECTATION' ? 'Rôles affectés' : 'Rôles retirés' }}
                  <span class="roles-count-badge">{{ extractedRoles.length }}</span>
                </div>
                <!-- Type de mapping (client ou realm) -->
                <div class="roles-context">
                  <span class="roles-context-label">{{ getResourceTypeLabel(data.resourceType) }}</span>
                </div>
                <!-- Liste des rôles sous forme de chips -->
                <div class="roles-list">
                  @for (role of extractedRoles; track role) {
                    <div
                      class="role-chip"
                      [class.role-chip--affectation]="data.operationType === 'AFFECTATION'"
                      [class.role-chip--retrait]="data.operationType === 'RETRAIT'">
                      <span class="role-chip-icon">
                        <!-- Icône selon type AFFECTATION/RETRAIT -->
                        @if (data.operationType === 'AFFECTATION') {
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5V19M5 12H19" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                          </svg>
                        }
                        @if (data.operationType === 'RETRAIT') {
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path d="M5 12H19" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/>
                          </svg>
                        }
                      </span>
                      <span class="role-chip-name">{{ role.name }}</span>
                      @if (role.clientRole) {
                        <span class="role-chip-tag">Client</span>
                      }
                      @if (!role.clientRole && data.resourceType === 'REALM_ROLE_MAPPING') {
                        <span class="role-chip-tag realm">Realm</span>
                      }
                    </div>
                  }
                </div>
              }
    
              <!-- Cas où la représentation est vide mais c'est bien une op sur rôle -->
              @if (isRoleOperation && extractedRoles.length === 0) {
                <div class="section-title">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M12 11V16M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                    <path d="M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  {{ data.operationType === 'AFFECTATION' ? 'Rôles affectés' : 'Rôles retirés' }}
                </div>
                <div class="no-roles-msg">Détail des rôles non disponible pour cet événement.</div>
              }

              @if (data.operationType === 'UPDATE' && !data.changes?.length && hasEventRepresentation) {
                <details class="debug-repr">
                  <summary>Voir les données brutes</summary>
                  <pre>{{ data.details['oldRepresentation'] | json }}</pre>
                  <pre>{{ data.details['representation'] | json }}</pre>
                </details>
              }
    
              <!-- ── Informations de l'événement ── -->
              <!-- <div class="section-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 5.55228 9.44772 6 10 6H14C14.5523 6 15 5.55228 15 5M9 5C9 4.44772 9.44772 4 10 4H14C14.5523 4 15 4.44772 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
                  stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                Informations de l'événement
              </div>
              <div class="details-grid">
                <div class="detail-row">
                  <span class="detail-key">Type ressource</span>
                  <span class="detail-val mono">{{ data.resourceType || '—' }}</span>
                </div>
                <div class="detail-row">
                  <span class="detail-key">Opération</span>
                  <span class="detail-val mono">{{ data.operationType || '—' }}</span>
                </div>
              </div> -->
    
              <!-- ── Connexion ── -->
              <!-- <div class="section-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" stroke-width="1.5"/>
                <path d="M2 12H22M12 2C9.33 6.67 9.33 17.33 12 22M12 2C14.67 6.67 14.67 17.33 12 22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
              Connexion
            </div>
            <div class="details-grid">
              <div class="detail-row">
                <span class="detail-key">Date & heure</span>
                <span class="detail-val">{{ formatDate(data.timestamp) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-key">Adresse IP</span>
                <span class="detail-val mono">{{ data.ipAddress || 'Non disponible' }}</span>
              </div>
            </div> -->
    
          </mat-dialog-content>
    
          <!-- ── Footer ── -->
          <div class="dialog-footer">
            <button class="close-action-btn" mat-dialog-close>Fermer</button>
          </div>
    `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
      background: #ffffff;
      border-radius: 16px;
      overflow: hidden;
    }

    /* ── Header ── */
    .dialog-header {
      display: flex;
      align-items: center;
      gap: 14px;
      padding: 20px 24px 16px;
      border-bottom: 1px solid #f0f0f0;
    }
    .header-icon-wrap {
      flex-shrink: 0;
      width: 44px; height: 44px;
      border-radius: 12px;
      background: linear-gradient(135deg, #e8f0fe 0%, #d2e3fc 100%);
      color: #1a73e8;
      display: flex; align-items: center; justify-content: center;
    }
    .header-text { display: flex; flex-direction: column; gap: 2px; flex: 1; }
    .header-title  { font-size: 17px; font-weight: 700; color: #1a1a2e; letter-spacing: -0.2px; }
    .header-subtitle { font-size: 12px; color: #9aa0ac; }
    .close-btn {
      flex-shrink: 0;
      width: 32px; height: 32px; border-radius: 8px;
      border: none; background: #f5f5f5; color: #666;
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: background 0.15s, color 0.15s;
    }
    .close-btn:hover { background: #ffe8e8; color: #d93025; }

    /* ── Bannière opération ── */
    .operation-banner {
      display: flex; align-items: center; justify-content: space-between;
      padding: 10px 24px; font-size: 13px;
    }
    .operation-banner[data-type="AFFECTATION"] { background: #e6f4ea; }
    .operation-banner[data-type="RETRAIT"]      { background: #fef7e0; }
    .operation-banner[data-type="CREATE"]       { background: #e8f0fe; }
    .operation-banner[data-type="UPDATE"]       { background: #e8f0fe; }
    .operation-banner[data-type="DELETE"],
    .operation-banner[data-type="SUPPRESSION"]  { background: #fce8e6; }
    .op-label { font-weight: 600; letter-spacing: 0.2px; }
    .operation-banner[data-type="AFFECTATION"] .op-label { color: #137333; }
    .operation-banner[data-type="RETRAIT"]      .op-label { color: #b06000; }
    .operation-banner[data-type="CREATE"]       .op-label { color: #1a73e8; }
    .operation-banner[data-type="UPDATE"]       .op-label { color: #1a73e8; }
    .operation-banner[data-type="DELETE"]       .op-label,
    .operation-banner[data-type="SUPPRESSION"]  .op-label { color: #c5221f; }
    .op-type {
      font-size: 11px; font-family: 'Courier New', monospace;
      padding: 2px 8px; border-radius: 4px;
      background: rgba(0,0,0,0.06); color: #444;
    }

    /* ── Body ── */
    .dialog-body {
      padding: 20px 24px !important;
      max-height: 62vh; overflow-y: auto;
      display: flex; flex-direction: column; gap: 20px;
    }
    .dialog-body::-webkit-scrollbar { width: 4px; }
    .dialog-body::-webkit-scrollbar-thumb { background: #e0e0e0; border-radius: 4px; }

    /* ── Titres de section ── */
    .section-title {
      display: flex; align-items: center; gap: 8px;
      font-size: 11px; font-weight: 700;
      text-transform: uppercase; letter-spacing: 0.9px;
      color: #9aa0ac; margin-bottom: -8px;
    }
    .section-title svg { flex-shrink: 0; }
    .section-title--add   { color: #137333; }
    .section-title--add svg { color: #137333; }
    .section-title--remove { color: #b06000; }
    .section-title--remove svg { color: #b06000; }

    /* Compteur de rôles à côté du titre */
    .roles-count-badge {
      margin-left: 4px;
      display: inline-flex; align-items: center; justify-content: center;
      min-width: 18px; height: 18px; padding: 0 5px;
      border-radius: 9px;
      font-size: 10px; font-weight: 700;
      background: #e6f4ea; color: #137333;
    }
    .section-title--remove .roles-count-badge {
      background: #fef7e0; color: #b06000;
    }

    /* Contexte (type de mapping) */
    .roles-context {
      display: flex; align-items: center; gap: 8px;
      margin-top: -4px;
    }
    .roles-context-label {
      font-size: 11px; font-weight: 600;
      color: #9aa0ac; letter-spacing: 0.4px;
    }

    /* ── Chips de rôles ── */
    .roles-list {
      display: flex; flex-wrap: wrap; gap: 8px;
    }
    .role-chip {
      display: inline-flex; align-items: center; gap: 6px;
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 13px; font-weight: 600;
      border: 1.5px solid transparent;
      transition: transform 0.1s, box-shadow 0.1s;
    }
    .role-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,0.08); }

    /* Chip AFFECTATION : vert */
    .role-chip--affectation {
      background: #e6f4ea;
      color: #137333;
      border-color: #b7dfbe;
    }
    /* Chip RETRAIT : orange/ambre */
    .role-chip--retrait {
      background: #fef7e0;
      color: #8a5000;
      border-color: #f5d78e;
    }

    .role-chip-icon { display: flex; align-items: center; flex-shrink: 0; }
    .role-chip-name { font-family: 'Courier New', monospace; font-size: 12.5px; letter-spacing: 0.2px; }

    /* Badge "Client" ou "Realm" sur la chip */
    .role-chip-tag {
      display: inline-flex; align-items: center;
      font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;
      padding: 1px 5px; border-radius: 4px;
      background: rgba(0,0,0,0.06); color: inherit;
      margin-left: 2px;
    }
    .role-chip-tag.realm { background: rgba(26, 115, 232, 0.1); color: #1a73e8; }

    /* Message aucun rôle */
    .no-roles-msg {
      font-size: 13px; color: #9aa0ac;
      padding: 10px 14px;
      background: #f8f9fa; border-radius: 10px;
      border: 1px dashed #e0e0e0;
    }

    /* ── Cartes acteurs ── */
    .cards-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .info-card {
      display: flex; align-items: center; gap: 12px;
      padding: 14px 16px; border-radius: 12px;
      border: 1px solid #f0f0f0; background: #fafafa;
      transition: border-color 0.15s;
    }
    .info-card:hover { border-color: #d0d8f0; background: #f5f7ff; }
    .card-icon {
      flex-shrink: 0; width: 36px; height: 36px;
      border-radius: 10px; display: flex; align-items: center; justify-content: center;
    }
    .author-icon { background: #e8f0fe; color: #1a73e8; }
    .target-icon  { background: #e6f4ea; color: #137333; }
    .card-content { display: flex; flex-direction: column; gap: 2px; overflow: hidden; }
    .card-label {
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.6px; color: #9aa0ac;
    }
    .card-value {
      font-size: 13px; font-weight: 600; color: #1a1a2e;
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }

    /* ── Grille de détails ── */
    .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .detail-row {
      display: flex; flex-direction: column; gap: 3px;
      padding: 12px 14px; border-radius: 10px;
      background: #f8f9fa; border: 1px solid transparent;
      transition: border-color 0.15s;
    }
    .detail-row:hover { border-color: #e8ecf0; }
    .detail-row.full { grid-column: 1 / -1; }
    .detail-key {
      font-size: 10px; font-weight: 600; text-transform: uppercase;
      letter-spacing: 0.6px; color: #9aa0ac;
    }
    .detail-val { font-size: 13px; font-weight: 500; color: #2d3748; word-break: break-word; }
    .detail-val.mono { font-family: 'Courier New', monospace; font-size: 12px; color: #3c4043; }

    /* ── Footer ── */
    .dialog-footer {
      padding: 14px 24px 18px;
      display: flex; justify-content: flex-end;
      border-top: 1px solid #f0f0f0;
    }
    .close-action-btn {
      padding: 9px 24px; border-radius: 8px;
      border: 1.5px solid #dadce0; background: white;
      font-size: 13px; font-weight: 600; color: #3c4043;
      cursor: pointer; transition: background 0.15s, border-color 0.15s;
    }
    .close-action-btn:hover { background: #f8f9fa; border-color: #bcc0c4; }

    .changes-list { display: flex; flex-direction: column; gap: 6px; }

    .change-row {
      display: flex; align-items: center; gap: 12px;
      padding: 10px 14px;
      background: #fafafa;
      border: 1px solid #f0f0f0;
      border-left: 3px solid #dadce0;
      border-radius: 8px;
    }
    .change-row[data-category="SECURITY"]    { border-left-color: #c5221f; }
    .change-row[data-category="IDENTITY"]    { border-left-color: #1a73e8; }
    .change-row[data-category="CONTACT"]     { border-left-color: #1a73e8; }
    .change-row[data-category="PERMISSIONS"] { border-left-color: #137333; }
    .change-row[data-category="ATTRIBUTES"]  { border-left-color: #b06000; }

    .change-main { flex: 1; display: flex; flex-direction: column; gap: 3px; }
    .change-label { font-size: 11px; text-transform: uppercase;
                    letter-spacing: .6px; color: #9aa0ac; font-weight: 700; }

    .change-values { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

    .change-old {
      padding: 2px 8px; border-radius: 6px;
      background: #fce8e6; color: #c5221f;
      font-family: 'Courier New', monospace; font-size: 12px;
      text-decoration: line-through;
    }
    .change-new {
      padding: 2px 8px; border-radius: 6px;
      background: #e6f4ea; color: #137333;
      font-family: 'Courier New', monospace; font-size: 12px;
    }
    .change-new--empty { font-style: italic; opacity: .7; text-decoration: none; }

    .change-arrow { color: #9aa0ac; font-weight: 700; }

    .change-type-badge {
      font-size: 10px; font-weight: 700;
      text-transform: uppercase; letter-spacing: .4px;
      padding: 3px 8px; border-radius: 10px;
    }
    .change-type-badge[data-type="MODIFIED"] { background:#e8f0fe; color:#1a73e8; }
    .change-type-badge[data-type="ADDED"]    { background:#e6f4ea; color:#137333; }
    .change-type-badge[data-type="REMOVED"]  { background:#fce8e6; color:#c5221f; }
    .change-type-badge[data-type="REVOKED"]  { background:#fce8e6; color:#c5221f; }
    .change-type-badge[data-type="GRANTED"]  { background:#e6f4ea; color:#137333; }

    .operation-banner[data-type="ACTION"] { background: #ede7f6; }
    .operation-banner[data-type="ACTION"] .op-label { color: #512da8; }

    .action-detail {
      padding: 14px 16px;
      border-radius: 10px;
      background: #f8f9fa;
      border: 1px solid #f0f0f0;
    }
    .action-detail-title { font-size: 14px; font-weight: 700; color: #1a1a2e; }
    .action-detail-code  { font-size: 11px; color: #9aa0ac; margin-top: 4px; }
    .action-detail-code code {
      font-family: 'Courier New', monospace;
      background: #eef0f2;
      padding: 1px 6px; border-radius: 4px;
    }

    .action-detail-subtitle {
      font-size: 11px;
      font-weight: 400;
      color: #9aa0ac;
      margin-left: 4px;
    }
  `]
})
export class AdminAuditDetailDialogComponent implements OnInit {

  /** Rôles extraits du champ details.representation */
  extractedRoles: ExtractedRole[] = [];

  /** Vrai si l'opération concerne une affectation ou un retrait de rôle/groupe */
  isRoleOperation = false;

  loading = true;

  constructor(
    public dialogRef: MatDialogRef<AdminAuditDetailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdminAuditEntry
  ) { }

  ngOnInit(): void {
    this.isRoleOperation = this.data.operationType === 'AFFECTATION'
      || this.data.operationType === 'RETRAIT';

    if (this.isRoleOperation) {
      this.extractedRoles = this.extractRoles(this.data.details);
    }

    setTimeout(() => (this.loading = false), 200);
  }

  // ================================================================
  // EXTRACTION DES RÔLES
  //
  // details.representation contient une chaîne JSON.
  // Pour AFFECTATION et RETRAIT, c'est un tableau d'objets rôle :
  //   [{ "id": "...", "name": "CPS_ADMIN", "composite": false, "clientRole": true }]
  //
  // On parse ce tableau et on retourne une liste propre d'ExtractedRole.
  // Si le parsing échoue ou si ce n'est pas un tableau, on retourne [].
  // ================================================================

  private extractRoles(details: Record<string, any> | null | undefined): ExtractedRole[] {
    if (!details?.['representation']) return [];

    try {
      const parsed = JSON.parse(details['representation']);

      if (!Array.isArray(parsed)) return [];

      return parsed.map((r: any) => ({
        name: r.name ?? r.id ?? '(inconnu)',
        clientRole: r.clientRole === true,
        composite: r.composite === true
      }));
    } catch {
      return [];
    }
  }

  // ================================================================
  // UTILITAIRES D'AFFICHAGE
  // ================================================================

  getResourceTypeLabel(resourceType: string): string {
    const map: Record<string, string> = {
      CLIENT_ROLE_MAPPING: 'Rôle client',
      REALM_ROLE_MAPPING: 'Rôle realm',
      GROUP_MEMBERSHIP: 'Groupe'
    };

    return map[resourceType] ?? resourceType;
  }

  formatDate(timestamp: number): string {
    if (!timestamp) return 'N/A';
    return new Date(timestamp)
      .toLocaleString('fr-FR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      })
      .replace(',', ' à');
  }

  getOperationLabel(operationType: string): string {
    const map: Record<string, string> = {
      AFFECTATION: 'Affectation de rôle',
      RETRAIT: 'Retrait de rôle',
      CREATE: 'Création',
      UPDATE: 'Modification',
      DELETE: 'Suppression',
      SUPPRESSION: 'Suppression'
    };

    return map[operationType] ?? operationType;
  }

  getChangeTypeLabel(type: string): string {
    return {
      MODIFIED: 'Modifié',
      ADDED: 'Ajouté',
      REMOVED: 'Supprimé',
      REVOKED: 'Révoqué',
      GRANTED: 'Attribué'
    }[type] ?? type;
  }

  get hasEventRepresentation(): boolean {
    const d = this.data.details || {};
    return Boolean(d['representation'] || d['oldRepresentation']);
  }

  get hasBothRepresentations(): boolean {
    const d = this.data.details || {};
    return Boolean(d['representation'] && d['oldRepresentation']);
  }

  get hasAnyRepresentation(): boolean {
    const d = this.data.details || {};
    return Boolean(d['representation'] || d['oldRepresentation']);
  }
}